/**
 * TriggerPreview - Compact preview of the trigger (email, email thread, or CRM event)
 *
 * Displays the email or CRM event that triggered a proposal, shown inside
 * ActionOverview's expanded panel, above MultiActionTimeline.
 */

import { useEffect, useRef } from 'react';

import type { ProposalDetail } from '@/lib/proposals/core/types';
import {
  isCrmEventTrigger,
  isEmailTrigger,
  isHighPriorityTrigger,
} from '@/lib/proposals/core/types';
import {
  getTriggerActionIds,
  useTriggerPreview,
} from '@/lib/proposals/hooks/useTriggerPreview';

import CrmEventTriggerPreview, {
  CrmEventTriggerPreviewSkeleton,
} from './CrmEventTriggerPreview';
import { EmailTriggerCard } from './EmailTriggerCard';
import { TimelineConnector } from './shared';

interface TriggerPreviewProps {
  /** The proposal containing the email action */
  proposal: ProposalDetail;
  /** Callback when user clicks to view full thread. When undefined, the "View Thread" button is hidden. */
  onOpenThread?: () => void;
  /** Whether this action has trigger data (controls skeleton display) */
  hasTrigger: boolean;
  /**
   * Whether each trigger card starts expanded. Default false (collapsed) for
   * the standalone proposal-panel layout; ActionOverview passes true so the
   * cards show their full detail when the overview is opened.
   */
  defaultExpanded?: boolean;
  /**
   * Whether the LAST trigger draws its connector line. Default true, since in
   * the standalone proposal panel that line links down to the action timeline
   * below. Callers with nothing beneath the triggers (e.g. ActionOverview)
   * pass false so the final connector doesn't dangle. Connectors between
   * triggers are always drawn.
   */
  showTrailingConnector?: boolean;
  /**
   * Which triggers to render. 'all' (default) renders every trigger.
   * 'high-priority' renders only high-priority triggers (see
   * `isHighPriorityTrigger`); 'standard' renders everything else.
   * ActionOverview uses this to render high-priority triggers outside its
   * accordion.
   */
  include?: 'all' | 'high-priority' | 'standard';
  /**
   * Trigger action ids to fetch/cache under, overriding derivation from
   * `proposal`. When `proposal` is a "visible" view with no-op actions filtered
   * out, its `actions[0]` may no longer be the trigger's source action — so the
   * caller passes ids derived from the ORIGINAL (unfiltered) proposal to keep
   * the trigger fetch correct. See {@link getTriggerActionIds}.
   */
  triggerActionIds?: { fetchActionId: number; cacheActionId: number };
}

/**
 * Loading skeleton for trigger preview
 * Matches the exact structure and spacing of the rendered component
 */
function TriggerPreviewSkeleton() {
  return (
    <div className='mb-0'>
      <div className='w-full rounded-xl bg-primary border border-primary shadow-xs p-4 animate-pulse'>
        <div className='flex items-start gap-3'>
          {/* Avatar skeleton */}
          <div className='flex-shrink-0'>
            <div className='w-8 h-8 bg-secondary rounded-full' />
          </div>
          <div className='flex-1 min-w-0'>
            {/* Line 1: From · Subject + View Thread + Time + Chevron */}
            <div className='flex items-center gap-2 mb-0.5'>
              <div className='flex items-center gap-1.5 min-w-0 flex-1'>
                <div className='h-3.5 bg-secondary rounded w-20' />
                <div className='h-3.5 bg-secondary rounded w-32' />
              </div>
              <div className='flex items-center gap-2'>
                <div className='h-5 bg-secondary rounded-full w-16' />
                <div className='h-3 bg-secondary rounded w-8' />
                <div className='w-4 h-4 bg-secondary rounded' />
              </div>
            </div>
            {/* Snippet: 3 lines to match typical content height */}
            <div className='mt-1 space-y-0.5'>
              <div className='h-3 bg-secondary rounded w-full' />
              <div className='h-3 bg-secondary rounded w-full' />
              <div className='h-3 bg-secondary rounded w-3/4' />
            </div>
          </div>
        </div>
      </div>
      {/* Connecting line to action below */}
      <TimelineConnector />
    </div>
  );
}

/**
 * Compact preview card showing one or more triggers
 */
export default function TriggerPreview({
  proposal,
  onOpenThread,
  hasTrigger,
  defaultExpanded = false,
  showTrailingConnector = true,
  include = 'all',
  triggerActionIds,
}: TriggerPreviewProps) {
  // Trigger data availability is determined by the API, not action type.
  // Both ids are 0 when there is no action, which disables the query. Fetch the
  // current version, cache under the root — see getTriggerActionIds. Must match
  // ActionOverview exactly or the two stop deduping onto one request. Prefer the
  // caller-supplied ids (derived from the original, unfiltered proposal) so
  // hiding no-op actions can't shift which action the trigger fetch keys on.
  const { fetchActionId, cacheActionId } =
    triggerActionIds ?? getTriggerActionIds(proposal);

  // Fetch trigger data - hook is always called but query is disabled when no action
  const {
    data: triggers,
    isLoading,
    isError,
  } = useTriggerPreview(proposal.org, proposal.id, fetchActionId, {
    enabled: fetchActionId > 0,
    cacheActionId,
  });

  // Track whether we showed a skeleton so we know whether to fade in.
  // If data was available immediately (cached/prefetched), render instantly.
  // Driven from an effect so the ref only updates on committed renders.
  const didShowSkeletonRef = useRef(false);
  useEffect(() => {
    if (isLoading && hasTrigger) {
      didShowSkeletonRef.current = true;
    }
  }, [isLoading, hasTrigger]);

  // Show skeleton only while loading AND if we expect trigger data
  if (isLoading && hasTrigger) {
    // Determine skeleton type based on source context channel
    const channel = proposal.source_context?.channel;
    if (channel === 'crm_event') {
      return <CrmEventTriggerPreviewSkeleton />;
    }
    return <TriggerPreviewSkeleton />;
  }

  // Hide entirely when no trigger data exists (graceful degradation)
  if (
    isError ||
    !triggers ||
    !Array.isArray(triggers) ||
    triggers.length === 0
  ) {
    return null;
  }

  // Narrow to the requested subset (high-priority triggers vs. the rest).
  let visibleTriggers = triggers;
  if (include === 'high-priority') {
    visibleTriggers = triggers.filter(isHighPriorityTrigger);
  } else if (include === 'standard') {
    visibleTriggers = triggers.filter(
      trigger => !isHighPriorityTrigger(trigger)
    );
  }

  if (visibleTriggers.length === 0) {
    return null;
  }

  // The header count should reflect only triggers that actually render a
  // card below — a non-renderable sentinel trigger type (neither CRM event
  // nor email) still counts toward `visibleTriggers.length` but skips
  // rendering, so counting it would show "N triggers" for a card that only
  // ever displays N-1.
  const renderableCount = visibleTriggers.filter(
    t => isCrmEventTrigger(t) || isEmailTrigger(t)
  ).length;

  return (
    <div
      className={
        didShowSkeletonRef.current
          ? 'animate-in fade-in duration-300'
          : undefined
      }
    >
      {renderableCount > 1 && (
        <div className='text-xs text-[var(--color-text-tertiary)] mb-2'>
          {renderableCount} triggers
        </div>
      )}
      {visibleTriggers.map((trigger, index) => {
        // Connectors link each card to the next. The last card's connector
        // links down to the action timeline in the standalone panel, but
        // dangles when there's nothing below (showTrailingConnector=false).
        const isLast = index === visibleTriggers.length - 1;
        const showConnector = !isLast || showTrailingConnector;

        if (isCrmEventTrigger(trigger)) {
          return (
            <CrmEventTriggerPreview
              key={trigger.crm_event.entity_id}
              trigger={trigger}
              orgId={proposal.org}
              showConnector={showConnector}
              defaultExpanded={defaultExpanded}
              onOpenThread={onOpenThread}
            />
          );
        } else if (isEmailTrigger(trigger)) {
          // Email trigger
          return (
            <EmailTriggerCard
              key={trigger.email.message_id}
              trigger={trigger}
              onOpenThread={onOpenThread}
              showConnector={showConnector}
              defaultExpanded={defaultExpanded}
            />
          );
        } else {
          // Unknown trigger type - skip rendering
          return null;
        }
      })}
    </div>
  );
}
